import { authConfig } from '../utils/config';

const baseURL = authConfig.authUrl;

// Decode a JWT payload client-side (no signature verification — just reading claims).
const decodeJwtPayload = (token: string): any => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
};

// The login/2FA response body's user has only {id,email,opsAssociations}; the
// effective permission keys live in the JWT `permissions` claim. Copy them onto the
// user so AuthContext / hasPermission can read them.
const attachPermissionsFromToken = (data: any): void => {
  if (data?.token && data.user) {
    const payload = decodeJwtPayload(data.token);
    data.user.permissions = Array.isArray(payload.permissions)
      ? payload.permissions
      : [];
  }
};

// Custom login function that gets JWT from Lambda
export const loginWithJWT = async (email: string, password: string) => {
  try {
    const response = await fetch(`${baseURL}/auth/sign-in/email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
      }),
    });

    if (!response.ok) {
      throw new Error('Login failed');
    }

    const { data } = await response.json();

    // Store JWT token in localStorage for API requests
    if (data.token) {
      // Ensure opsAssociations is in array format before storing
      if (data.user && data.user.opsAssociations) {
        if (typeof data.user.opsAssociations === 'string') {
          data.user.opsAssociations = data.user.opsAssociations
            .split(',')
            .map((s: string) => s.trim())
            .filter((s: string) => s); // Remove empty strings
        }
      }
      
      attachPermissionsFromToken(data);
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_data', JSON.stringify(data.user));
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Custom logout function
export const logoutJWT = () => {
  localStorage.clear();
};

// 2FA API methods
export const enable2FA = async (tempToken: string, password: string) => {
  try {
    const response = await fetch(`${baseURL}/auth/2fa/enable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tempToken,
        password,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to enable 2FA');
    }

    return await response.json();
  } catch (error) {
    console.error('Enable 2FA error:', error);
    throw error;
  }
};

export const verify2FASetup = async (setupToken: string, code: string) => {
  try {
    const response = await fetch(`${baseURL}/auth/2fa/verify-enable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        setupToken,
        code,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to verify 2FA setup');
    }

    const { data } = await response.json();

    // Convert opsAssociations from string to array if needed
    if (data.user && data.user.opsAssociations) {
      if (typeof data.user.opsAssociations === 'string') {
        data.user.opsAssociations = data.user.opsAssociations
          .split(',')
          .map((s: string) => s.trim())
          .filter((s: string) => s); // Remove empty strings
      }
    }

    // Store JWT token and user data after successful 2FA setup
    if (data.token) {
      attachPermissionsFromToken(data);
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_data', JSON.stringify(data.user));
    }

    return data;
  } catch (error) {
    console.error('Verify 2FA setup error:', error);
    throw error;
  }
};

export const verify2FALogin = async (tempToken: string, code: string) => {
  try {
    const response = await fetch(`${baseURL}/auth/2fa/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tempToken,
        code,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to verify 2FA login');
    }

    const { data } = await response.json();

    // Convert opsAssociations from string to array if needed
    if (data.user && data.user.opsAssociations) {
      if (typeof data.user.opsAssociations === 'string') {
        data.user.opsAssociations = data.user.opsAssociations
          .split(',')
          .map((s: string) => s.trim())
          .filter((s: string) => s); // Remove empty strings
      }
    }

    // Store JWT token and user data after successful 2FA verification
    if (data.token) {
      attachPermissionsFromToken(data);
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_data', JSON.stringify(data.user));
    }

    return data;
  } catch (error) {
    console.error('Verify 2FA login error:', error);
    throw error;
  }
};

export const get2FAStatus = async () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token');
    }

    const response = await fetch(`${baseURL}/auth/2fa/status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get 2FA status');
    }

    const { data } = await response.json();
    return data;
  } catch (error) {
    console.error('Get 2FA status error:', error);
    throw error;
  }
};

export const generateBackupCodes = async () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token');
    }

    const response = await fetch(`${baseURL}/auth/2fa/backup-codes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to generate backup codes');
    }

    const { data } = await response.json();
    return data;
  } catch (error) {
    console.error('Generate backup codes error:', error);
    throw error;
  }
};
